import type { CardVendor, ImageResource } from '@meizhen/shared'
import type { ProductVendor } from '@meizhen/shared/src/types/productVendor'
import { clone } from 'ramda'

export interface CinemaSetting {
  id: number
  cinemaId: number
  ct: number
  mt: number
  buyTip: string // 购票提示
  watchTip: string // 观影提示
  balanceLimit: number // 余额限制
  rechargeLimit: number // 充值限制
  rechargeLowerLimit: number // 充值下限
  onlineRechargeTip: string // 充值提示
  /** SDK版本 */
  catName: string
  cardVendor: CardVendor // 卡片供应商标识
  productVendor: ProductVendor // 卖品供应商标识
  _balanceLimit: string
  _rechargeLimit: string
  _rechargeLowerLimit: string
  /** 购票确认 */
  seatConfirmTip: SeatConfirmTip
  /** 购票限制 */
  memberTicketLimit: number
  showMall: boolean
  wxUserName: string
  wxAppId: string
  /** 是否开通会员功能 */
  openMember: boolean
  // [ZHONGXIN-INTERCEPTION] 拦截配置字段
  interceptZhongxinApi?: boolean // 拦截总开关
  interceptZhongxinApiCinema?: boolean // 拦截影院开关
  interceptZhongxinApiPhoneNumbers?: string[] // 不拦截手机号列表
  interceptZhongxinApiTimePeriods?: TimePeriod[] // 拦截时间段列表
  interceptExcludedFilms?: InterceptionExcludedFilm[] // 拦截排除影片列表
}

export interface SeatConfirmTip {
  tp: number
  content?: string
  image?: ImageResource[]
}

export interface MallJump {
  showMall: boolean
  wxUserName: string
  wxAppId: string
}

export function isSameSeatConfirmTip(a?: SeatConfirmTip, b?: SeatConfirmTip) {
  if (a == null || b == null) {
    return false
  }
  return a.tp === b.tp
    && a.content === b.content
    && (a.image != null && a.image?.length > 0 && a.image[0].id) === (b.image != null && b.image?.length > 0 && b.image[0].id)
}

export function isSameMallJump(a?: MallJump, b?: MallJump) {
  if (a == null || b == null) {
    return false
  }
  return a.showMall === b.showMall
    && a.wxUserName === b.wxUserName
    && a.wxAppId === b.wxAppId
}

export enum ConfirmTp {
  film = 1,
  custom = 2,
}

export function covertFormToParams(form: Partial<CinemaSetting>) {
  const params = clone(form)
  params.balanceLimit = toFen(params._balanceLimit)
  params.rechargeLimit = toFen(params._rechargeLimit)
  params.rechargeLowerLimit = toFen(params._rechargeLowerLimit)
  delete params._balanceLimit
  delete params._rechargeLimit
  delete params._rechargeLowerLimit
  return params
}

export function covertDataToForm(form: Partial<CinemaSetting>) {
  const params = clone(form)
  params._balanceLimit = formatPrice(params.balanceLimit, false, false)
  params._rechargeLimit = formatPrice(params.rechargeLimit, false, false)
  params._rechargeLowerLimit = formatPrice(params.rechargeLowerLimit, false, false)
  return params
}

export enum SyncTp {
  film = 3,
  price = 4,
}

export interface SyncUpstreamInfo {
  cat: SyncTp
  doneTick: number
  startTick: number
  status: number
}

const mainCinemaProd = 1
const mainCinemaBeta = 10
const mainCinema = serverProd ? mainCinemaProd : mainCinemaBeta
export const isMainCinemaForFilmInfo = (id: number) => mainCinema === id

// [ZHONGXIN-INTERCEPTION-START] 拦截配置相关类型

export interface TimePeriod {
  startTime: string // HH:MM 格式
  endTime: string // HH:MM 格式
  daysOfWeek: number[] // 1-7，1=周一，7=周日
}

export interface InterceptionExcludedFilm {
  filmCode: string
  filmName: string
}

export interface InterceptionConfig {
  interceptZhongxinApi: boolean // 拦截总开关
  interceptZhongxinApiCinema: boolean // 拦截影院开关
  interceptZhongxinApiPhoneNumbers: string[] // 不拦截手机号列表
  interceptZhongxinApiTimePeriods: TimePeriod[] // 拦截时间段列表
  interceptExcludedFilms: InterceptionExcludedFilm[] // 拦截排除影片列表
  seatTaskProtectionDisabled: boolean // 是否关闭占座任务队列保护
  seatTaskMaxPendingCount: number // 待领取任务数量阈值
  seatTaskMaxPendingSeconds: number // 最老待领取任务等待秒数阈值
  seatTaskMaxProcessingSeconds: number // 最老处理中任务处理秒数阈值
  seatTaskMaxConsecutiveFailed: number // 连续失败熔断阈值
  seatTaskApiKey: string // 客户端任务接口 API Key，仅初始化/重置后返回完整值
  seatTaskApiKeyPrefix: string // 客户端任务接口 API Key 脱敏展示
  seatTaskApiKeyUpdatedAt: number // 客户端任务接口 API Key 更新时间
  seatTaskApiKeyUpdatedBy: number // 客户端任务接口 API Key 更新人
  runtimeStatus?: InterceptionRuntimeStatus
}

export interface SeatTaskApiKey {
  key: string
  prefix: string
  updatedAt: number
  updatedBy: number
}

export interface InterceptionRuntimeStatus {
  status: 'normal' | 'degraded' | 'fused'
  reason: string
  reasonDetail: string
  lastDegradedAt?: number
  lastFusedAt?: number
  lastResumeAt?: number
  manualResumeBy: number
  protectionDisabled: boolean
  pendingCount: number
  processingCount: number
  oldestPendingAgeSeconds: number
  oldestProcessingAgeSeconds: number
  consecutiveFailedCount: number
}

export interface UpdateInterceptionSeatTaskProtectionParams {
  seatTaskProtectionDisabled: boolean
  seatTaskMaxPendingCount: number
  seatTaskMaxPendingSeconds: number
  seatTaskMaxProcessingSeconds: number
  seatTaskMaxConsecutiveFailed: number
}

// [ZHONGXIN-INTERCEPTION-END]
